/**
 * Guide announcement card — the standard in-chat announcement that a path
 * completion has earned the user a guide.
 *
 * NOT path-specific. Every completion surface that can produce a guide renders
 * this same block: the hardcoded Foundation completion, the Haiku-streamed
 * generic path completions, and anything added later. The producer side
 * (`buildGuideAnnouncementBlock`) and the renderer live in one module for the
 * same reason the Foundation bullets live beside their buttons — the tag shape
 * is a contract between them, and split across files it would drift.
 *
 * The card is CLIENT-SPLICED, never model-emitted. A completion renderer takes
 * the `guideCompletion.guides[]` summary the route hands back and asks for a
 * block; the model is not given a lever to announce a guide that does not
 * exist, nor to name one that does. The COMPLETION MESSAGE carries the
 * announcement language ("finishing this path unlocked your … guide") — the
 * prompt's `guide` context field and this block are populated under the same
 * predicate (this module's), so message and card cannot disagree. The card
 * itself is a bare artifact row, the same primitive behind
 * `<practice-reference>` / `<memo-reference>`: a chat card pointing at an
 * artifact that lives elsewhere in the app. The status chip is the shared
 * `.notification-label` contract (`components/notification-indicators.css`).
 *
 * Lifecycle: DELIBERATELY STATIC. The card renders whatever status the
 * completion envelope carried and never changes it — no polling, no
 * generating→ready transition. Generation runs detached for minutes while the
 * completion message ends with a menu of next actions, so a user who waits
 * around to watch a chip flip is not a user who exists; and a resolved card
 * scrolls out of reach the moment they reply. The durable channels (sidebar
 * badge, dashboard activity row, guide-ready email) are what actually carry
 * someone back to a finished guide. Polling for that unseen transition cost
 * ~25 `/guides/available` calls per completion, each evaluating every guide
 * definition.
 *
 * `ready` at render time is a real and useful case rather than a leftover: the
 * orchestrator short-circuits an already-complete path to `type: 'complete'`
 * (`_buildAlreadyCompleteResponse`), so RE-ENTERING a finished path replays its
 * completion message with the guide already written. That card is clickable
 * because the guide genuinely exists at that moment, and it is the only thing
 * on screen the returning user has not already read. The message copy (not a
 * band label) is what keeps a week-old guide from being called new — the
 * prompt's returning variant says "your guide is in your Library".
 *
 * @module components/guideAnnouncementCard
 */

import { eventBus } from '../core/utils.js';

const COPY = {
  generating: 'Ready in your Library shortly',
  ready: 'Ready — open it in your Library'
};

/**
 * The status chip per state. Text is lower-case to match every other
 * `.notification-label` in the app (`generating`, `error`, `unread`); the
 * component uppercases it in CSS. The Library's guide card carries the same
 * moments differently — "(writing)" on its You pill while generating, and an
 * actionable rail word from `InsightsSection._guideRailState` otherwise.
 */
const STATUS_CHIP = {
  generating: { text: 'generating', tone: 'orange' },
  ready: { text: 'ready', tone: 'green' }
};

/**
 * Escape the five XML metacharacters. Guide titles are authored (they come from
 * a definition's `titleTemplate`, not from user input), but this block is built
 * from server data and parsed back out with regexes, so it is escaped on the
 * way in regardless.
 *
 * @param {string} value
 * @returns {string}
 */
function escapeXml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

/**
 * Reverse {@link escapeXml} for a parsed field.
 * @param {string} value
 * @returns {string}
 */
function unescapeXml(value) {
  return String(value)
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&amp;/g, '&');
}

/**
 * Read a single child element's text from the block's inner content.
 * @param {string} innerContent
 * @param {string} tag
 * @returns {string|null}
 */
function parseField(innerContent, tag) {
  const match = innerContent.match(new RegExp(`<${tag}>([\\s\\S]*?)</${tag}>`));
  if (!match) return null;
  const value = unescapeXml(match[1].trim());
  return value || null;
}

/**
 * Build the `<guide-announcement>` block for a guide summary from a path
 * completion envelope, or null when no guide is owed.
 *
 * This is the ONLY place a completion renderer should construct the tag —
 * callers pass the raw summary and splice the returned string into the message
 * text as its own `\n\n`-delimited block. Callers that also feed the completion
 * MESSAGE a guide mention (the `topic_complete` intentContext `guide` field,
 * the Foundation pre-artifact copy) must gate that mention on this function
 * returning non-null, so the message never announces a card that won't render.
 *
 * @param {Object|null} guide - An entry from `guideCompletion.guides[]`:
 *   `{ id, title, status }`. Statuses:
 *     - `generating` — generation has just been kicked off.
 *     - `pending`    — the guide IS coming but eligibility has not caught up
 *                      yet; an l2 path's final ARTIFACT readiness is written by
 *                      the async discovery pipeline AFTER the completion request
 *                      returns, so the route sees a not-yet-eligible path for a
 *                      path the orchestrator just declared complete. Announced
 *                      exactly like `generating`.
 *     - `ready`      — a guide already exists (the re-entry case).
 *     - `locked`     — genuinely not owed (declined past the minimum, or not
 *                      visible). Returns null: nothing announces a guide that
 *                      is not coming.
 * @param {Object} [options]
 * @param {string|null} [options.image] - The backing PATH's `cardContent.image`
 *   filename (the completion envelope's `pathMetadata.cardContent.image`).
 *   Renders inside the tile in place of the generic book icon, tying the card
 *   to the path artwork the user just walked. Optional — absent falls back to
 *   the book icon.
 * @returns {string|null} The block, or null when there is nothing to announce.
 */
export function buildGuideAnnouncementBlock(guide, { image = null } = {}) {
  const raw = guide?.status;
  const status = raw === 'pending' ? 'generating' : raw;
  if (status !== 'generating' && status !== 'ready') return null;

  const title = guide?.title;
  if (!title) return null;

  const lines = [
    '<guide-announcement>',
    `<title>${escapeXml(title)}</title>`,
    `<status>${escapeXml(status)}</status>`
  ];
  if (guide.id) lines.push(`<guide-id>${escapeXml(guide.id)}</guide-id>`);
  if (image) lines.push(`<image>${escapeXml(image)}</image>`);
  lines.push('</guide-announcement>');

  return lines.join('\n');
}

/**
 * Render a `<guide-announcement>` block as a bare artifact row (no wrapper
 * band — the completion message carries the announcement language).
 *
 * Expected XML:
 *   <guide-announcement>
 *   <title>Starting Your Journey</title>
 *   <status>generating</status>
 *   <guide-id>guide-starting-your-journey</guide-id>
 *   </guide-announcement>
 *
 * (A legacy `<label>` child is tolerated and ignored — messages persisted
 * before the band was removed still parse.)
 *
 * @param {string} innerContent - Content between opening and closing tags
 * @param {Object} [options]
 * @param {boolean} [options.interactive=true] - False for static replay: no
 *   click target, matching how the other artifact cards degrade.
 * @returns {HTMLElement|null}
 */
export function renderGuideAnnouncement(innerContent, { interactive = true } = {}) {
  const title = parseField(innerContent, 'title');
  if (!title) return null;

  const status = parseField(innerContent, 'status') === 'ready' ? 'ready' : 'generating';
  const guideDefinitionId = parseField(innerContent, 'guide-id');
  const image = parseField(innerContent, 'image');

  // The artifact IS the root: `data-guide-state`, the CSS hooks, and the dev
  // inspector handle all live here now that the band wrapper is gone.
  const artifact = document.createElement('div');
  artifact.className = 'artifact artifact--guide-announcement';

  const inner = document.createElement('div');
  inner.className = 'artifact__inner';

  const left = document.createElement('div');
  left.className = 'artifact__left';

  const iconWrapper = document.createElement('div');
  iconWrapper.className = 'artifact__icon-wrapper';
  const icon = document.createElement('div');
  icon.className = 'artifact__icon';
  if (image) {
    // The path's card artwork fills the document tile. Built via DOM (never
    // innerHTML) so the server-authored filename cannot inject markup; the
    // path-card pattern (`assets/images/paths/<file>`) is reused verbatim.
    icon.classList.add('artifact__icon--image');
    const img = document.createElement('img');
    img.className = 'artifact__icon-img';
    img.alt = '';
    img.src = `assets/images/paths/${image}`;
    icon.appendChild(img);
  } else {
    icon.innerHTML = '<svg width="25" height="28" aria-hidden="true"><use href="#icon-guide"/></svg>';
  }
  iconWrapper.appendChild(icon);
  left.appendChild(iconWrapper);

  const content = document.createElement('div');
  content.className = 'artifact__content';

  const titleRow = document.createElement('div');
  titleRow.className = 'artifact__title-row';
  const titleEl = document.createElement('span');
  titleEl.className = 'artifact__title';
  titleEl.textContent = title;
  titleRow.appendChild(titleEl);
  content.appendChild(titleRow);

  const subtitle = document.createElement('div');
  subtitle.className = 'artifact__subtitle';
  subtitle.textContent = status === 'ready' ? COPY.ready : COPY.generating;
  content.appendChild(subtitle);

  left.appendChild(content);
  inner.appendChild(left);

  // Status slot: the in-progress pill and the ready chevron are siblings that
  // swap, so the cross-fade has something to fade between.
  const actions = document.createElement('div');
  actions.className = 'artifact__actions artifact__actions--guide-status';

  // Text and tone are set by setState, which owns every per-state difference.
  const pill = document.createElement('span');
  pill.className = 'notification-label artifact__guide-status';
  actions.appendChild(pill);

  const chevron = document.createElement('span');
  chevron.className = 'artifact__guide-chevron';
  chevron.innerHTML = `
    <svg class="artifact__chevron" width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M7.5 5L12.5 10L7.5 15" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>`;
  actions.appendChild(chevron);

  inner.appendChild(actions);
  artifact.appendChild(inner);

  const clickable = interactive && !!guideDefinitionId;

  // Attached once and gated on the rendered state, rather than added and removed
  // per state. Keeps listener membership constant so the dev inspector can flip
  // the card either way with no bookkeeping to get wrong.
  if (clickable) {
    // Routes through the same `open_guides` action the completion buttons use,
    // so leaving the live chat session stays ActionRouter's business.
    const open = () => {
      if (artifact.dataset.guideState !== 'ready') return;
      // `action` must ride INSIDE actionData — executeAction validates
      // actionData.action; the top-level actionId is only a log label.
      eventBus.emit('action:button-click', {
        actionId: 'open_guides',
        actionData: { action: 'open_guides', guideDefinitionId }
      });
    };
    artifact.addEventListener('click', open);
    artifact.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      if (artifact.dataset.guideState !== 'ready') return;
      e.preventDefault();
      open();
    });
  }

  /**
   * Set the card's rendered state. The single writer of `data-guide-state`,
   * which the CSS and the click gate both read.
   *
   * Nothing in the app calls this twice: the card renders the status the
   * completion envelope carried and stays there. It exists as a function rather
   * than inline setup so the dev inspector can preview both states on one card
   * (`dev.setGuideCardState`), and so the two states cannot drift apart.
   *
   * @param {'generating'|'ready'} next
   */
  const setState = (next) => {
    if (artifact.dataset.guideState === next) return;
    artifact.dataset.guideState = next;

    subtitle.textContent = COPY[next] || COPY.generating;

    const chip = STATUS_CHIP[next] || STATUS_CHIP.generating;
    pill.className = `notification-label notification-label--${chip.tone} artifact__guide-status`;
    pill.textContent = chip.text;

    const isTarget = next === 'ready' && clickable;
    artifact.classList.toggle('artifact--clickable', isTarget);
    if (isTarget) {
      artifact.setAttribute('role', 'button');
      artifact.setAttribute('tabindex', '0');
    } else {
      artifact.removeAttribute('role');
      artifact.removeAttribute('tabindex');
    }
  };

  // Dev inspector hook: `dev.setGuideCardState(...)` flips the card already on
  // screen so both states can be eyeballed without re-streaming the completion
  // message. Nothing in the app reads this.
  artifact.__guideAnnouncement = { setState, guideDefinitionId };

  setState(status);

  return artifact;
}

/**
 * Flatten a `<guide-announcement>` block to plain text, for the non-rendered
 * fallback path (plain-text serialization of a message).
 *
 * @param {string} match - The full matched block including tags
 * @returns {string}
 */
export function flattenGuideAnnouncement(match) {
  const title = parseField(match, 'title');
  if (!title) return '';
  const statusKey = parseField(match, 'status') === 'ready' ? 'ready' : 'generating';
  return `\n**${title}** — ${COPY[statusKey]}\n`;
}

export const __testables = { COPY };
